import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { playVictoryOnce } from '@/lib/victorySound';
import { playChessMoveSound, playChessCaptureSound } from '@/lib/chessSounds';
import CaptureActivityPlayer from '@/components/studies/CaptureActivityPlayer';
import PlayComputerPlayer from '@/components/studies/PlayComputerPlayer';
import LiveStudentBoards from '@/components/classroom/LiveStudentBoards';
import PuzzleReplay from '@/components/classroom/PuzzleReplay';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, Trophy, X, Clock, Timer, PlayCircle } from 'lucide-react';

// Stable bridge component: keeps a memoized `activity` object so the player's
// internal `useEffect([open, initialPieces])` only re-runs when a new puzzle
// is sent (puzzle_id changes) — NOT on every polling tick. Without this the
// student's in-progress pieces would be reset every ~500ms.
const CaptureActivityBridge = React.memo(function CaptureActivityBridge({ roomId, puzzleId, title, instructions, config, durationSeconds, startedAt }) {
  const activity = React.useMemo(() => ({
    id: puzzleId,
    title: title || 'Capture Activity',
    instructions: instructions || '',
    capture_config: config,
    duration_seconds: durationSeconds,
    started_at: startedAt,
  }), [puzzleId]);
  // Push live progress to the classroom so the teacher's monitor updates in
  // real time. Fire-and-forget; failures don't block the student.
  const onProgress = React.useCallback((p) => {
    if (!roomId) return;
    api.post(`/classrooms/${roomId}/capture-progress`, p).catch(() => {});
  }, [roomId]);
  // Render INLINE inside the Classroom "Ejercicios" area (no modal). The
  // student stays on this activity — even after solving — until the teacher
  // pulses "Cerrar ejercicio" (server sets active_puzzle:None → LivePuzzle
  // unmounts → this bridge unmounts → student returns to explanation board).
  //
  // key={puzzleId}: when the teacher launches a DIFFERENT Capture Activity
  // WITHOUT first closing the current one, active_puzzle.id changes but the
  // bridge keeps mounting the same CaptureActivityPlayer instance — so the
  // internal `won` state that renders the green "¡MISIÓN CUMPLIDA!" overlay
  // stays true across puzzles and covers the new activity. React key
  // remounts the child on id change, guaranteeing every fresh Capture
  // Activity starts with won=false, pieces=initialPieces, moves=0 etc. —
  // exactly the same reset useEffect at CaptureActivityPlayer:222 already
  // does, just triggered idiomatically instead of relying on effect deps.
  return (
    <CaptureActivityPlayer
      key={puzzleId}
      open={true}
      inline
      activity={activity}
      onProgress={onProgress}
    />
  );
});

export default function LivePuzzle({ room, isTeacher, currentUserId, expanded = false, boardWidth: externalBoardWidth = null, fitHeight = false, embedded = false }) {
  const p = room?.active_puzzle;
  const [game, setGame] = useState(null);
  const [solved, setSolved] = useState(false);
  const [moves, setMoves] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [expired, setExpired] = useState(false);
  const [attemptsUsed, setAttemptsUsed] = useState(0);
  const [locked, setLocked] = useState(false);
  const [solIndex, setSolIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [replayOpen, setReplayOpen] = useState(false);
  // Click-to-move state (Method 1). Drag & drop (Method 2) is untouched.
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalDests, setLegalDests] = useState({}); // { dst: { isCapture: bool } }
  // Last-move highlight (visual only). Uses the REAL from/to of executed moves so
  // wrong attempts (which get reverted) don't leave a false highlight.
  const [lastMove, setLastMove] = useState(null); // { from, to }
  const baseFenRef = useRef(null);
  const [boardW, setBoardW] = useState(300);
  const roRef = useRef(null);
  const boardWrapRef = useCallback((node) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (node) {
      const maxW = expanded ? 900 : 300;
      const minW = expanded ? 260 : 200;
      const measure = () => {
        if (externalBoardWidth) {
          if (fitHeight) {
            const availableW = node.clientWidth;
            const availableH = node.clientHeight;
            setBoardW(Math.max(160, Math.min(externalBoardWidth, availableW, availableH)));
          } else {
            setBoardW(externalBoardWidth);
          }
          return;
        }
        const availableW = node.clientWidth;
        const targetW = Math.min(maxW, availableW);
        setBoardW(Math.max(160, targetW));
      };
      measure();
      roRef.current = new ResizeObserver(measure);
      roRef.current.observe(node);
    }
  }, [expanded, externalBoardWidth, fitHeight]);

  useEffect(() => {
    if (p) {
      // Capture Activities never touch chess.js — their position is not a
      // legal chess game (may lack kings) and would throw "Invalid FEN:
      // missing white king". The CaptureActivityPlayer render path below
      // handles them exclusively. Skip the whole chess.js init.
      if (p.capture_config) return;
      setGame(new Chess(p.fen));
      baseFenRef.current = p.fen;
      setSolved(false); setMoves([]); setExpired(false); setSolIndex(0); setBusy(false);
      setLastMove(null); setSelectedSquare(null); setLegalDests({});
      const mine = (room.puzzle_solves || []).filter(s => s.user_id === currentUserId);
      const failed = mine.filter(s => !s.correct).length;
      const won = mine.some(s => s.correct);
      setAttemptsUsed(failed);
      setSolved(won);
      setLocked(won || failed >= (p.max_attempts || 3));
    }
  }, [p?.id]); // eslint-disable-line

  // Stage 3.2.2 · Fase C — cerrar Replay al cambiar/terminar el ejercicio. El
  // árbol oficial pertenece a un active_puzzle.id concreto: si cambia o
  // desaparece, se cierra el Replay para no mostrar la solución anterior.
  useEffect(() => { setReplayOpen(false); }, [p?.id]); // eslint-disable-line

  // Countdown
  useEffect(() => {
    if (!p) return;
    const started = new Date(p.started_at).getTime();
    const dur = (p.duration_seconds || 60) * 1000;
    const tick = () => {
      const r = Math.max(0, Math.round((started + dur - Date.now()) / 1000));
      setRemaining(r);
      if (r <= 0) setExpired(true);
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [p?.id, p?.started_at, p?.duration_seconds]);

  if (!p) return null;
  // Fase PC-5B: Play with Computer enviado desde Classroom.
  // El profesor lo envió vía POST /classrooms/{rid}/puzzle con type='play_computer'
  // (PC-5A). Aquí lo detectamos ANTES del branch de capture/puzzle para que
  // caiga en el runner correcto. Reutilizamos PlayComputerPlayer (PC-1/2/4)
  // pasándole activity=active_puzzle y studyId=active_puzzle.study_id.
  if (p.type === 'play_computer') {
    if (isTeacher) {
      // El profesor sigue viendo su tablero de explicación en el área principal.
      // No renderizamos el runner del alumno aquí. Un monitor específico
      // vendrá en PC-5D. Ofrecemos un pequeño panel con "Terminar ejercicio".
      return (
        <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-xs text-indigo-800 space-y-2" data-testid="pc-teacher-sidebar">
          <div className="font-semibold">♟️ Play with Computer en curso</div>
          <div>Los alumnos están jugando contra el motor.</div>
          <div className="text-[11px] opacity-75">{p.title || 'Play with Computer'} · {p.points} pts · nivel {p.engine_level}</div>
          <Button data-testid="pc-teacher-end" size="sm" variant="outline" className="w-full"
            onClick={async ()=>{ try { await api.post(`/classrooms/${room.id}/puzzle/end`); toast.success('Ejercicio cerrado'); } catch { toast.error('No se pudo cerrar'); } }}>
            <X className="w-3.5 h-3.5 mr-1"/> Cerrar ejercicio
          </Button>
        </div>
      );
    }
    return (
      <div className="w-full min-w-0 space-y-2" data-testid="pc-student-wrap">
        <PlayComputerPlayer
          open={true}
          inline
          onClose={()=>{}}
          activity={p}
          studyId={p.study_id}
          roomId={room?.id}
        />
      </div>
    );
  }
  // Capture Activities sent via ASK carry a capture_config on active_puzzle.
  // Route them to the CaptureActivityPlayer (reused as an inline modal) so the
  // student gets the exact same piece rules, obstacles, targets and sound the
  // teacher's board uses. Scoring/attempts/timer stay disabled per spec.
  if (p.capture_config) {
    // Teacher: KEEP the explanation board (main area) intact and render a
    // compact monitor card here in the sidebar with:
    //   · Activity title
    //   · "Terminar ejercicio" button (POSTs /puzzle/end → sets
    //     active_puzzle: None → students exit the modal automatically and
    //     everyone falls back to the teacher's explanation board, whose FEN
    //     was never touched by ASK)
    //   · LiveStudentBoards (real-time captured / moves / status)
    // ASK NEVER mutates the teacher's board FEN, arrows, circles, orientation
    // or history — only active_puzzle is written on the server, and the
    // main-area board conditional in ClassroomLive.jsx no longer swaps for
    // teachers when active_puzzle is set.
    if (isTeacher) {
      // Teacher's Capture Activity monitoring now lives in the WIDE
      // TeacherCaptureLiveView rendered in the classroom's main area (see
      // ClassroomLive.jsx > teacherCaptureMonitorMode). The sidebar entry
      // point returns null so we don't render a duplicate compact card.
      return null;
    }
    // NOTE: the activity object MUST be stable across renders. Every polling
    // tick brings a fresh `room` (and therefore a fresh `p.capture_config`
    // reference) even though the content is identical. If we pass a new
    // object literal each render, CaptureActivityPlayer's `useEffect(...,
    // [open, initialPieces])` fires on every poll and RESETS the student's
    // in-progress pieces back to the initial config — that is why the
    // student's moves visually snap back and captures never register.
    return <CaptureActivityBridge roomId={room?.id} puzzleId={p.id} title={p.title} instructions={p.instructions} config={p.capture_config} durationSeconds={p.duration_seconds} startedAt={p.started_at}/>;
  }

  const maxAttempts = p.max_attempts || 3;
  const solLen = (p.solution_moves?.length) || p.solution_len || 0;

  const onDrop = (from, to) => {
    if (isTeacher || !game || solved || expired || locked || busy || solLen === 0) return false;
    // Clear selection/hints as soon as a move is attempted from any method
    setSelectedSquare(null);
    setLegalDests({});
    // Apply the student's move locally (must be legal in the current position)
    const tmp = new Chess(game.fen());
    let move; try { move = tmp.move({ from, to, promotion: 'q' }); } catch { return false; }
    if (!move) return false;
    setGame(tmp);
    setLastMove({ from: move.from, to: move.to }); // reflect the accepted move visually
    setBusy(true);
    // Server validates this single move against the solution (real chess logic, solution hidden)
    api.post(`/classrooms/${room.id}/puzzle/attempt`, {
      src: from, dst: to, promotion: move.promotion || null, index: solIndex,
    }).then((res) => {
      const d = res.data || {};
      if (d.correct) {
        if (d.opponent) {
          // auto-play the opponent's reply after a short delay
          setTimeout(() => {
            setGame((g) => {
              const ng = new Chess(g.fen());
              try { ng.move(d.opponent.san); } catch { try { ng.move({ from: d.opponent.from, to: d.opponent.to, promotion: d.opponent.promotion || 'q' }); } catch { /* ignore */ } }
              baseFenRef.current = ng.fen();
              return ng;
            });
            setLastMove({ from: d.opponent.from, to: d.opponent.to });
            setSolIndex(d.next_index ?? (solIndex + 2));
            if (d.done) { setSolved(true); setLocked(true); finishToast(d); }
            else setBusy(false);
          }, 450);
        } else {
          baseFenRef.current = tmp.fen();
          setSolIndex(d.next_index ?? (solIndex + 2));
          if (d.done) { setSolved(true); setLocked(true); finishToast(d); }
          else setBusy(false);
        }
      } else {
        // wrong -> revert to the last good position, count the attempt, drop the false highlight
        setGame(new Chess(baseFenRef.current || p.fen));
        setLastMove(null);
        const left = d.attempts_left ?? Math.max(0, maxAttempts - (attemptsUsed + 1));
        setAttemptsUsed(maxAttempts - Math.max(0, left));
        if (d.locked || left <= 0) { setLocked(true); toast.error('Sin intentos restantes'); }
        else toast.error(`❌ Movimiento incorrecto. Te quedan ${left} intento(s)`);
        setBusy(false);
      }
    }).catch(() => {
      setGame(new Chess(baseFenRef.current || p.fen));
      setBusy(false);
    });
    return true;
  };

  const finishToast = (d) => {
    const pa = d.points_awarded ?? p.points; const b = d.bonus || 0;
    toast.success(`¡Correcto! +${pa} pts${b ? ` (incluye +${b} bonus)` : ''}`);
    // Play the victory chime EXCLUSIVELY on the student's device the moment
    // the server confirms the puzzle is done. Teachers open LivePuzzle in
    // observer mode (isTeacher=true) — they never reach here anyway (guard
    // at the top of tryMove) but we double-check for safety. Keyed by the
    // puzzle id so React re-renders / polling never re-trigger it.
    if (!isTeacher) playVictoryOnce(`puzzle:${p?.id}`);
  };

  // ----- Click-to-move (Method 1) — ends in the same onDrop() as drag & drop -----
  // Computes the legal moves for the currently selected piece using chess.js
  // (the very same engine that already validates local moves) so students see
  // exactly the destinations they can play. Clicking a non-legal square just
  // clears the selection — it does NOT count as an incorrect attempt.
  const computeLegalDests = (sq) => {
    if (!game) return {};
    try {
      const mv = game.moves({ square: sq, verbose: true });
      const dests = {};
      mv.forEach(m => { dests[m.to] = { isCapture: !!(m.flags && (m.flags.includes('c') || m.flags.includes('e'))) }; });
      return dests;
    } catch { return {}; }
  };

  const handleSquareClick = (sq) => {
    if (isTeacher || !game || solved || expired || locked || busy || solLen === 0) return;
    if (selectedSquare) {
      if (sq === selectedSquare) { setSelectedSquare(null); setLegalDests({}); return; }
      if (legalDests[sq]) {
        // Click-to-move path bypasses ChessBoardView.handlePieceDrop (which is
        // the drag & drop branch that plays the sound). Reuse the SAME central
        // sound module so a valid click-move sounds identical to a drag-move:
        // detect capture BEFORE calling onDrop (which mutates game state), then
        // fire the sound ONLY if onDrop returned truthy (server-validated).
        let wasCapture = false;
        try {
          const dstPiece = game.get(sq);
          if (dstPiece) wasCapture = true;
          else {
            const srcPiece = game.get(selectedSquare);
            if (srcPiece && srcPiece.type === 'p' && selectedSquare[0] !== sq[0]) wasCapture = true;
          }
        } catch { /* silent — falls back to move sound */ }
        const ok = onDrop(selectedSquare, sq);
        if (ok) (wasCapture ? playChessCaptureSound : playChessMoveSound)();
        return;
      }
      const piece = game.get(sq);
      if (piece && piece.color === game.turn()) {
        setSelectedSquare(sq);
        setLegalDests(computeLegalDests(sq));
        return;
      }
      setSelectedSquare(null); setLegalDests({});
      return;
    }
    const piece = game.get(sq);
    if (!piece || piece.color !== game.turn()) return;
    const dests = computeLegalDests(sq);
    if (Object.keys(dests).length === 0) return;
    setSelectedSquare(sq);
    setLegalDests(dests);
  };

  // Visual styles for last move + selection + legal-move hints (Lichess-inspired)
  const hintStyles = {};
  if (lastMove) {
    // Semi-transparent amber that reads well over both light & dark squares
    hintStyles[lastMove.from] = { backgroundColor: 'rgba(255, 210, 63, 0.45)' };
    hintStyles[lastMove.to]   = { backgroundColor: 'rgba(255, 210, 63, 0.55)' };
  }
  if (selectedSquare) {
    // Selection wins over the last-move tint on that same square
    hintStyles[selectedSquare] = { backgroundColor: 'rgba(255, 236, 100, 0.75)' };
  }
  Object.entries(legalDests).forEach(([dst, meta]) => {
    // Preserve any existing background (last-move) by merging via a fallback layer
    const base = hintStyles[dst] || {};
    hintStyles[dst] = {
      ...base,
      // Unified visual language: crisp defined ring for captures, small dot
      // for empty squares. Matches Capture Activity + teacher's explanation
      // board so kids see the same indicator everywhere.
      background: meta.isCapture
        ? 'radial-gradient(circle, transparent 0 88%, rgba(0,0,0,0.42) 88% 96%, transparent 96% 100%)'
        : 'radial-gradient(circle, rgba(0,0,0,0.28) 0 18%, transparent 18% 100%)',
    };
  });

  const end = async () => { await api.post(`/classrooms/${room.id}/puzzle/end`); };

  const solves = (room.puzzle_solves || []).filter(s => s.correct);
  const pctTime = p.duration_seconds ? Math.min(100, (remaining / p.duration_seconds) * 100) : 0;
  const timerColor = remaining <= 5 ? 'bg-rose-500' : remaining <= 15 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div data-testid="live-puzzle" className={embedded ? "w-full h-full min-h-0 flex flex-col overflow-hidden" : "rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-white p-4 mb-3"}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-amber-500 text-white grid place-items-center"><Trophy className="w-4 h-4"/></div>
          <div>
            <div className="font-display font-bold">Puzzle en vivo</div>
            <div data-testid="live-puzzle-title" className="text-xs text-slate-500">{p.title} · +{p.points} pts</div>
          </div>
        </div>
        {isTeacher && (
          <Button data-testid="end-puzzle-btn" size="sm" className="bg-rose-500 hover:bg-rose-600 text-white" onClick={end}><X className="w-4 h-4 mr-1"/> Terminar ejercicio</Button>
        )}
      </div>

      {/* Countdown */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-xs font-mono mb-1">
          <span className="inline-flex items-center gap-1">
            <Timer className={`w-3.5 h-3.5 ${expired ? 'text-rose-500' : 'text-slate-500'}`}/>
            {expired ? 'Tiempo agotado' : `${remaining}s`}
          </span>
          <span className="text-slate-500">{p.duration_seconds}s totales</span>
        </div>
        <div className="h-1.5 bg-white/70 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${timerColor}`} style={{width:`${pctTime}%`}}/>
        </div>
      </div>

      {!isTeacher && game && (
        <div className={`grid grid-cols-1 gap-3 ${embedded && fitHeight ? "flex-1 min-h-0 overflow-hidden" : ""}`}>
          {solved || locked || expired ? (
            <div className="flex flex-col items-center gap-3 py-4">
              <div data-testid="live-puzzle-status" className={`text-center font-semibold ${(expired && !solved) || (locked && !solved) ? 'text-rose-600' : 'text-emerald-700'}`}>
                {solved ? (<><CheckCircle2 className="w-8 h-8 mx-auto mb-1"/> ¡Lo resolviste! +{p.points} pts</>)
                  : expired ? '⏰ Se acabó el tiempo'
                  : '❌ Sin intentos restantes'}
              </div>
              {(solved || locked) && (
                <Button data-testid="reproducir-solucion-btn" size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                  onClick={() => setReplayOpen(true)}>
                  <PlayCircle className="w-4 h-4 mr-1"/> Reproducir solución
                </Button>
              )}
            </div>
          ) : (
            <div className={`w-full flex flex-col items-center ${embedded && fitHeight ? "flex-1 min-h-0 justify-start" : ""}`}>
              <div className="text-xs text-center text-slate-500 mb-4 shrink-0">Intentos: {maxAttempts - attemptsUsed} de {maxAttempts}</div>
              <div className="text-sm font-semibold text-slate-700 text-center mb-3 shrink-0">{p.fen.split(' ') [1] === 'w' ? '♔ Juegas con Blancas' : '♚ Juegas con Negras'}</div>
              <div ref={boardWrapRef} className={`w-full flex justify-center min-h-0 ${embedded && fitHeight ? "flex-1 overflow-hidden" : ""}`}>
                <ChessBoardView fen={game.fen()} onPieceDrop={onDrop} boardWidth={boardW}
                  onSquareClick={handleSquareClick}
                  extraSquareStyles={hintStyles}
                  orientation={p.fen.split(' ')[1] === 'w' ? 'white' : 'black'}/>
              </div>
            </div>
          )}
        </div>
      )}
      {isTeacher && (
        <div className="mt-2">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">
            {solves.length} de {room.participants.filter(x=>x.role!=='teacher').length} alumnos han resuelto
          </div>
          <div className="space-y-1">
            {solves.map((s, i) => (
              <div key={s.user_id} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-1.5 border border-slate-100">
                <span className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full grid place-items-center text-white text-xs font-bold ${i===0?'bg-amber-500':i===1?'bg-slate-400':'bg-orange-400'}`}>{i+1}</span>
                  <span className="font-semibold">{s.name}</span>
                </span>
                <span className="text-xs text-slate-500 font-mono flex items-center gap-1">
                  <Clock className="w-3 h-3"/>{Math.round((s.elapsed_ms||0)/1000)}s
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {replayOpen && p && (
        <PuzzleReplay roomId={room.id} activePuzzleId={p.id} onClose={() => setReplayOpen(false)} />
      )}
    </div>
  );
}
